/* src/ui/hud/moneyTicker.ts — the balance, ticking (TASKS.md 2.7).
 *
 * The one number the player watches. It updates on an animation frame from
 * sim/incomeInterpolator.ts rather than on the poll, because a balance that only moves every
 * few seconds reads as broken — and because the whole point of the tycoon loop is *seeing*
 * money arrive.
 *
 * Two hot text nodes, written directly. No diff, no re-render: this component updates several
 * times a second next to a WebGL canvas, and `.textContent =` is the cheapest possible write
 * (PLAN.md Part 4 §2).
 */

import { incomeInterpolator } from '../../sim/incomeInterpolator.ts';
import { Component } from '../component.ts';
import { el, text } from '../dom.ts';
import { formatMoney, formatRate } from '../format.ts';

export class MoneyTicker extends Component {
  #value = text('—');
  #rate = text('');
  #raf = 0;
  #lastValue = '';
  #lastRate = '';

  constructor() {
    super({});
  }

  protected render(): HTMLElement {
    return el(
      'div',
      { class: 'gf-hud-money', data: { action: 'hud:money' } },
      el('span', { class: 'gf-hud-label' }, 'MONEY'),
      el('span', { class: 'gf-hud-value' }, this.#value),
      el('span', { class: 'gf-hud-rate' }, this.#rate),
    );
  }

  protected override onMount(): void {
    const frame = (): void => {
      this.#raf = window.requestAnimationFrame(frame);
      if (!incomeInterpolator.primed) return;

      /*
       * Only touch the DOM when the rendered string actually changes. At 60fps the cents digit
       * changes far less often than the frame does, so this skips the overwhelming majority of
       * writes — and a `.textContent` assignment invalidates layout even when the value is
       * identical.
       */
      const next = formatMoney(incomeInterpolator.value());
      if (next !== this.#lastValue) {
        this.#value.textContent = next;
        this.#lastValue = next;
      }
      const rate = formatRate(incomeInterpolator.netPerMin);
      if (rate !== this.#lastRate) {
        this.#rate.textContent = rate;
        this.#lastRate = rate;
      }
    };
    frame();
    this.onCleanup(() => {
      window.cancelAnimationFrame(this.#raf);
    });
  }
}
